// BRL price formatting helpers.
//
// The price input on the listing form is a free-form string, so users may type:
//   "200", "1500", "1500.50", "1500,50", "R$ 200", "1.500,00", "200/h", "consultar"
// We try to parse a leading number and format it as Brazilian currency
// (`R$ 1.000,00`); anything trailing (e.g. "/h") is preserved.

const BRL = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const BRL_INT = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

interface FormatBRLOpts {
  compact?: boolean;
}

/**
 * Format a numeric value as Brazilian currency: 1000 → "R$ 1.000,00".
 * Returns null when the value is not a finite number.
 */
export function formatBRL(value: number | string, opts: FormatBRLOpts = {}): string | null {
  const { compact = false } = opts;
  const n = typeof value === "number" ? value : Number(String(value).replace(",", "."));
  if (!Number.isFinite(n)) return null;
  return compact && Number.isInteger(n) ? BRL_INT.format(n) : BRL.format(n);
}

interface ParsePriceResult {
  num: number | null;
  rest: string;
}

/**
 * Parse a free-form price string into { num, rest }.
 * "R$ 1.500,00 / hora" → { num: 1500, rest: "/ hora" }
 */
function parsePrice(raw: unknown): ParsePriceResult {
  const s = String(raw || "").trim();
  if (!s) return { num: null, rest: "" };

  const body = s.replace(/^r\$\s*/i, "");
  const m = body.match(/^[\d.,\s]+/);
  if (!m) return { num: null, rest: body };

  const numStr = m[0].trim();
  let normalized: string;
  if (numStr.includes(",") && numStr.includes(".")) {
    normalized = numStr.replace(/\./g, "").replace(",", ".");
  } else if (numStr.includes(",")) {
    normalized = numStr.replace(",", ".");
  } else if (/^\d{1,3}(\.\d{3})+$/.test(numStr)) {
    normalized = numStr.replace(/\./g, "");
  } else {
    normalized = numStr;
  }

  const num = parseFloat(normalized);
  if (!Number.isFinite(num)) return { num: null, rest: body };

  const rest = body.slice(m[0].length).trim();
  return { num, rest };
}

/**
 * Format a free-form price string as BRL, preserving any trailing unit
 * (e.g. "200/h" → "R$ 200,00/h"). Falls back to the cleaned raw text
 * when the string isn't numeric.
 */
export function formatPrice(raw: unknown): string {
  const { num, rest } = parsePrice(raw);
  if (num != null) {
    const formatted = BRL.format(num);
    return rest ? `${formatted} ${rest}` : formatted;
  }
  const cleaned = String(raw || "").trim().replace(/^r\$\s*/i, "");
  return cleaned;
}

interface PriceDisplayResult {
  label: string;
  value: string;
  kind: "range" | "from" | "upto";
}

/**
 * Returns null when both values are empty, otherwise:
 *   { label, value, kind: "range" | "from" | "upto" }
 */
export function formatPriceDisplay(
  starting: unknown,
  max: unknown,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _opts: Record<string, unknown> = {},
): PriceDisplayResult | null {
  const a = formatPrice(starting);
  const b = formatPrice(max);

  if (a && b) {
    return { label: "Valor", value: `${a} — ${b}`, kind: "range" };
  }
  if (a) {
    return { label: "A partir de", value: a, kind: "from" };
  }
  if (b) {
    return { label: "Até", value: b, kind: "upto" };
  }
  return null;
}

/**
 * Format a price range string like "150-200" or "650,00-1200" as BRL.
 * Falls back to the original text when parsing fails.
 */
export function formatPriceRange(value: string): string {
  if (!value) return "";
  const cleaned = value.trim();
  const parts = cleaned.split(/\s*[-–—]\s*/);
  if (parts.length >= 2) {
    const a = formatBRL(parts[0]);
    const b = formatBRL(parts[parts.length - 1]);
    if (a && b) return `${a} - ${b}`;
  }
  const single = formatBRL(cleaned);
  if (single) return single;
  return cleaned;
}